import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, LogOut, User, ChevronDown, ChevronLeft, ChevronRight, BarChart3, Settings, CreditCard, Link2 } from 'lucide-react';
import { useAffiliateAuth } from '../../contexts/AffiliateAuthContext';
import { saveLanguage } from '../../utils/storage';
import ScaleFlowLogo from '../Layout/ScaleFlowLogo.jsx';
import AffiliateSettingsModal from './AffiliateSettingsModal.jsx';

const AffiliateLayout = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { affiliate, logout } = useAffiliateAuth();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setIsSidebarCollapsed(false);
        if (isMobileMenuOpen) setIsMobileMenuOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobileMenuOpen]);

  const navigation = [
    { name: t('affiliateDashboard.nav.dashboard'), id: 'dashboard', icon: BarChart3, href: '/affiliate/dashboard' },
    { name: t('affiliateDashboard.yourReferralLink'), id: 'referralLink', icon: Link2, href: '/affiliate/referral-link' },
    { name: t('affiliateDashboard.nav.payoutDetails'), id: 'payout', icon: CreditCard, href: '/affiliate/payout-details' },
  ];

  const handleNavigation = (item) => {
    setIsMobileMenuOpen(false);
    navigate(item.href);
  };

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    saveLanguage(language);
  };

  const handleLogout = () => {
    logout();
    navigate('/affiliate/login');
  };

  return (
    <div className={`min-h-screen bg-background ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      <div className={`fixed top-0 h-full bg-sidebar shadow-lg border-r border-sidebar-border z-40 transition-all duration-300 ease-in-out ${
        i18n.language === 'ar' ? 'right-0' : 'left-0'
      } ${
        isMobileMenuOpen ? 'translate-x-0' : i18n.language === 'ar' ? 'translate-x-full md:translate-x-0' : '-translate-x-full md:translate-x-0'
      } ${
        isSidebarCollapsed && !isMobile ? 'w-16' : 'w-64'
      }`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-sidebar-border bg-sidebar">
            <div className="flex flex-col space-y-4">
              <div className="flex items-center justify-between">
                {!isSidebarCollapsed && <ScaleFlowLogo showText={true} />}
                {isSidebarCollapsed && <ScaleFlowLogo showText={false} className="w-10 h-10" />}
                {!isMobile && (
                  <button
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    className="p-2 rounded-lg text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent transition-all duration-200 flex-shrink-0"
                    title={isSidebarCollapsed ? t('affiliateDashboard.expandSidebar') : t('affiliateDashboard.collapseSidebar')}
                  >
                    {i18n.language === 'ar'
                      ? (isSidebarCollapsed ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />)
                      : (isSidebarCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />)}
                  </button>
                )}
                {isMobile && (
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-lg text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent transition-all duration-200 flex-shrink-0"
                    title={t('common.closeMenu')}
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>

              {!isSidebarCollapsed && (
                <div className="flex items-center justify-end pt-2 border-t border-sidebar-border">
                  <div className="text-xs text-sidebar-foreground/60">
                    {i18n.language === 'ar' ? 'إصدار 1.0.0' : 'v1.0.0'}
                  </div>
                </div>
              )}
            </div>
          </div>

          <nav className="flex-1 p-4">
            <div className="space-y-1">
              {navigation.map((item) => {
                const IconComponent = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item)}
                    className={`group flex items-center w-full px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActive ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-lg shadow-sidebar-primary/25' : 'text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm'
                    } ${isSidebarCollapsed ? 'justify-center' : ''} ${i18n.language === 'ar' && !isSidebarCollapsed ? 'justify-end text-right' : ''}`}
                    title={isSidebarCollapsed ? item.name : ''}
                  >
                    <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'} ${isSidebarCollapsed ? 'justify-center' : ''} ${i18n.language === 'ar' && !isSidebarCollapsed ? 'justify-end text-right w-full' : ''}`}>
                      <IconComponent className={`h-5 w-5 ${isActive ? 'text-sidebar-primary-foreground' : 'text-sidebar-foreground/70 group-hover:text-sidebar-foreground'}`} />
                      {!isSidebarCollapsed && <span className="font-medium">{item.name}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </nav>

          {!isSidebarCollapsed && (
            <div className="p-4 border-t border-sidebar-border">
              <button
                onClick={() => {
                  setIsSettingsModalOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 text-sidebar-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent hover:shadow-sm ${i18n.language === 'ar' ? 'justify-end text-right' : ''}`}
              >
                <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3 justify-end text-right w-full' : 'space-x-3'}`}>
                  <Settings className="h-5 w-5 text-sidebar-foreground/70" />
                  <span className="font-medium">{t('settings.title')}</span>
                </div>
              </button>
            </div>
          )}

          <div className="p-4 border-t border-sidebar-border mt-auto">
            {!isSidebarCollapsed && (
              <div className="relative mb-3" ref={userMenuRef}>
                <button
                  className="w-full p-3 bg-sidebar-accent rounded-lg border border-sidebar-border hover:bg-sidebar-accent/80 transition-all duration-200 shadow-sm"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="true"
                >
                  <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                    <div className="w-10 h-10 bg-sidebar-primary/20 rounded-full flex items-center justify-center shadow-sm">
                      <User className="h-5 w-5 text-sidebar-primary" />
                    </div>
                    <div className={`flex-1 min-w-0 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
                      <p className="text-sm font-semibold text-sidebar-foreground truncate">
                        {affiliate?.username || t('affiliateDashboard.affiliateFallback')}
                      </p>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-sidebar-foreground/70 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                {isUserMenuOpen && (
                  <div className={`absolute ${i18n.language === 'ar' ? 'left-0' : 'right-0'} bottom-full mb-2 w-48 bg-card rounded-lg shadow-lg border border-border py-1 z-50`}>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsUserMenuOpen(false);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-sm text-card-foreground hover:bg-muted hover:text-destructive flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-2 text-right' : 'space-x-2 text-left'} transition-colors duration-200`}
                    >
                      <LogOut className="h-4 w-4" />
                      <span>{t('auth.signOut')}</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`${i18n.language === 'ar'
        ? (isSidebarCollapsed && !isMobile ? 'mr-16' : isMobile ? 'mr-0' : 'lg:mr-64')
        : (isSidebarCollapsed && !isMobile ? 'ml-16' : isMobile ? 'ml-0' : 'lg:ml-64')
      } transition-all duration-300`}>
        <main className="p-6 bg-background min-h-screen">
          <Outlet />
        </main>
      </div>

      <div className={`fixed top-4 z-30 md:hidden ${i18n.language === 'ar' ? 'left-4' : 'right-4'}`}>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-lg bg-card border border-border text-foreground hover:bg-muted transition-all duration-200"
          aria-label={t('common.toggleMenu')}
        >
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AffiliateSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        onLanguageChange={handleLanguageChange}
      />
    </div>
  );
};

export default AffiliateLayout;

